import { en as enLocalizations } from "common/config/localizations";
import type { Venue } from "common/types";
import type { PlacesCityParams, PlacesCityResponse, PlacesVenue } from "common/types/places";
import { sanitiseMetaDescription } from "@/lib/meta";
import { slugToTitle } from "@/lib/slug";

type CityData = PlacesCityResponse["data"];

const FILTER_KEYS = [
  "types",
  "accessibilities",
  "accessibility_sections",
  "openingHoursMode",
  "openingHoursDay",
  "openingHoursTime",
] as const;

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TYPES_PATTERN = /^[A-Za-z0-9_ &|-]+$/;
// The API's date_format:g:i A — no leading zero on the hour, upper-case AM/PM.
const TIME_PATTERN = /^(1[0-2]|[1-9]):[0-5]\d (AM|PM)$/;
const MAX_PARAM_LENGTH = 2000;

/** Fills "%{name}" placeholders; head() runs outside React, so it cannot use useI18N. */
export function fillTemplate(template: string, values: Record<string, string | number>): string {
  return template.replace(/%\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}

/**
 * Reads a city page's page and filters from the URL, dropping anything the API would reject, so a
 * mistyped filter shows the unfiltered city instead of a 404.
 *
 * @param raw TanStack search params.
 * @returns Every known key, undefined where the URL value was empty or rejected.
 */
export function parsePlacesSearch(raw: Record<string, unknown>): PlacesCityParams {
  const text = (value: unknown) => {
    // TanStack parses "?accessibilities=743" as the number 743.
    const str = typeof value === "number" && Number.isFinite(value) ? String(value) : value;
    return typeof str === "string" && str.trim() !== "" && str.trim().length <= MAX_PARAM_LENGTH
      ? str.trim()
      : undefined;
  };
  const matching = (value: string | undefined, pattern: RegExp) =>
    value !== undefined && pattern.test(value) ? value : undefined;

  const page = Number(raw.page);
  const day = text(raw.openingHoursDay);
  const time = matching(text(raw.openingHoursTime), TIME_PATTERN);
  const custom = raw.openingHoursMode === "custom" && day !== undefined && DAYS.includes(day) && time !== undefined;
  const mode = raw.openingHoursMode === "open_now" ? "open_now" : custom ? "custom" : undefined;

  // Keys stay present when undefined: TanStack lays this over the raw URL search, so an
  // omitted key would let the rejected raw value through to the loader.
  return {
    page: Number.isInteger(page) && page > 1 ? page : undefined,
    types: matching(text(raw.types), TYPES_PATTERN),
    accessibilities: text(raw.accessibilities),
    accessibility_sections: text(raw.accessibility_sections),
    openingHoursMode: mode,
    openingHoursDay: custom ? day : undefined,
    openingHoursTime: custom ? time : undefined,
  };
}

/**
 * City-page search params for Link / navigate. The app's router is registered untyped, so
 * TanStack types `search` as never; this keeps that one cast in a single place.
 *
 * @param search Page and filters.
 * @returns The same object, typed for the router.
 */
export function placesSearch(search: PlacesCityParams): never {
  return search as never;
}

/** Whether any filter narrows the city page (the page number does not count). */
export function hasPlacesFilters(search: PlacesCityParams): boolean {
  return FILTER_KEYS.some((key) => search[key] !== undefined && search[key] !== "");
}

/** Whether two city-page searches apply the same filters, ignoring the page number. */
export function samePlacesFilters(a: PlacesCityParams, b: PlacesCityParams): boolean {
  return FILTER_KEYS.every((key) => (a[key] ?? "") === (b[key] ?? ""));
}

/**
 * Head tags for /places.
 *
 * @param origin Canonical origin, no trailing slash.
 * @param isProduction Robots are only emitted in production; the root layout blocks the rest.
 * @returns Head meta and links.
 */
export function buildPlacesIndexHead(origin: string, isProduction: boolean) {
  return {
    meta: [
      { title: enLocalizations.placesIndexTitle },
      { name: "description", content: sanitiseMetaDescription(enLocalizations.placesIndexDescription) },
      ...(isProduction ? [{ name: "robots", content: "index, follow" }] : []),
    ],
    links: [{ rel: "canonical", href: `${origin}/places` }],
  };
}

/**
 * Head tags for /places/<city>: filtered views and small cities stay out of the index, and each
 * numbered page canonicalises to itself.
 *
 * @param data The city page data, or undefined when it failed to load.
 * @param slug City slug from the URL.
 * @param search Page and filters from the URL.
 * @param origin Canonical origin, no trailing slash.
 * @param isProduction Robots are only emitted in production; the root layout blocks the rest.
 * @returns Head meta and links.
 */
export function buildPlacesCityHead(
  data: CityData | undefined,
  slug: string,
  search: PlacesCityParams,
  origin: string,
  isProduction: boolean,
) {
  const cityName = data?.city.name ?? slugToTitle(slug);
  const page = search.page ?? 1;
  const filtered = hasPlacesFilters(search);

  const title = page > 1
    ? fillTemplate(enLocalizations.placesCityPagedTitle, { city: cityName, page })
    : fillTemplate(enLocalizations.placesCityTitle, { city: cityName });
  const description = sanitiseMetaDescription(
    fillTemplate(page > 1 ? enLocalizations.placesCityPagedDescription : enLocalizations.placesCityDescription, {
      city: cityName,
      count: data?.city.count ?? 0,
      page,
    }),
  );

  const cityUrl = `${origin}/places/${slug}`;
  const canonical = filtered || page === 1 ? cityUrl : `${cityUrl}?page=${page}`;
  const robots = !data || filtered || !data.city.indexable ? "noindex, follow" : "index, follow";

  return {
    meta: [
      { title },
      { name: "description", content: description },
      ...(isProduction ? [{ name: "robots", content: robots }] : []),
    ],
    links: [{ rel: "canonical", href: canonical }],
  };
}

/**
 * A city-page venue in the shape VenueCard reads.
 *
 * @param venue One venue from the places API.
 * @returns A Venue with the card's fields filled and the rest left empty.
 */
export function placesVenueToVenue(venue: PlacesVenue): Venue {
  return {
    id: String(venue.google_business_id),
    name: venue.name,
    address: venue.address,
    latitude: venue.latitude,
    longitude: venue.longitude,
    businessType: venue.business_type_label || venue.business_type,
    types: "",
    photo: venue.photo_thumb,
    photoThumb: venue.photo_thumb,
    altText: venue.alt_text,
    reviewDetailsSubmitted: venue.reviews_details_submitted,
    reviewsDetailsSubmitted: venue.reviews_details_submitted,
    venueDataSource: venue.venue_data_source ?? undefined,
    hasExperiences: venue.has_experiences ?? false,
    hasAlerts: venue.has_alerts ?? false,
    hasGeneralUserPhotos: venue.has_general_user_photos ?? false,
    hasGeneralUserTags: venue.has_general_user_tags ?? false,
    latestReviewDateUpdated: venue.latest_review_date_updated ?? "",
    openingHours: [],
    tagIds: "",
    taggingCategories: "",
    distance: "",
    userDistance: "",
    businessStatus: "OPERATIONAL",
    business: {
      id: venue.id,
      slug: venue.slug,
      publicUrl: venue.public_url || venue.slug,
      templateType: venue.template_type,
      isComplex: venue.template_type && venue.template_type !== "Basic" ? 1 : 0,
      venue_summary: venue.venue_summary ?? "",
    },
  } as unknown as Venue;
}
